import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';
import './Routines.scss';

import { Button } from 'primereact/button';
import { ServerResponsiveTable } from '../../components/ServerResponsiveTable/ServerResponsiveTable';
import { TableColumn, TableAction } from '../../types/responsiveTable';
import { useClient } from '../../hooks/useClient';
import { useOrganizationContext } from '../../context/OrganizationContext';
import useSWR, { mutate } from 'swr';
import { Routine, RoutineTableEntry } from '../../types/Fisiofit/routine';
import { differenceInDays, startOfDay } from 'date-fns';
import { PrimeIcons } from 'primereact/api';
import { RoutinePanel } from '../../components/RoutinePanel/RoutinePanel';
import { useResourcePanel } from '../../hooks/useResourcePanel';
import { showDeleteDialog } from '../../utils/dialogUtils';
import { useFeaturesToggle } from '../../hooks/useFeaturesToggle';
import { ALL_FEATURES } from '../../config/features';
import { AllowedFeatures } from '../../types/features';
import { useAuthContext } from '../../context/AuthContext';
import { Switch } from '../../components/Switch/Switch';
import { ExportButton } from '../../components/ExportButton/ExportButton';
import { useSearchParams } from 'react-router-dom';

export const getRoutineDeleteConfirmationDialogProps = (routine: Routine | undefined, isNextRoutine: boolean) => {
  return {
    message: (
      <>
        ¿Estás seguro de que quieres eliminar la {isNextRoutine ? 'próxima rutina' : 'rutina'}{' '}
        {routine?.externalUser ? (
          <>
            de{' '}
            <b>
              {routine?.externalUser?.name} {routine?.externalUser?.surnames}
            </b>
            ?<br />
            <br />
            La rutina se borrará por completo, y no aparecerá en ningún historial.
            {routine?.nextRoutine && !isNextRoutine && ' También se eliminará la próxima rutina.'}
            {routine?.nextRoutine && isNextRoutine && ' No se eliminará la rutina actual.'}
          </>
        ) : (
          <>
            <b>{routine?.name}</b>?
          </>
        )}
      </>
    ),
    header: 'Eliminar rutina',
  };
};

export const getRoutineDays = (startDate: string | undefined, duration: number | null) => {
  const daysDifference = startDate ? differenceInDays(startOfDay(new Date()), startOfDay(new Date(startDate))) : null;
  const elapsedDays = Math.max(0, Math.min(daysDifference ?? 0, duration ?? 0));
  const hasStarted = daysDifference !== null && daysDifference >= 0;
  const remainingDays = duration ? duration - elapsedDays : null;
  return { daysDifference, elapsedDays, hasStarted, remainingDays };
};

export const getRoutineDaysSeverity = (
  startDate: string | undefined,
  duration: number | null,
  hasNextRoutine: boolean,
  isExpired: boolean,
): 'warning' | 'error' | null => {
  const { remainingDays, hasStarted } = getRoutineDays(startDate, duration);
  if (isExpired) return 'error';
  if (!hasStarted) return null;
  if (hasNextRoutine) return null;
  if (remainingDays !== null && remainingDays <= 3) return 'error';
  if (remainingDays !== null && remainingDays <= 5) return 'warning';
  return null;
};

const getRowDaysRemainingSeverity = (_: any, row: Routine) => {
  return getRoutineDaysSeverity(row.localStartDate, row.duration, !!row.nextRoutine, !!row.isExpired);
};

export const Routines = () => {
  const { get, delete: del } = useClient();
  const { organization } = useOrganizationContext();
  const { user: authUser } = useAuthContext();
  const userHasEditFeature = useFeaturesToggle([ALL_FEATURES.ROUTINES_WRITE]);
  const [showOnlySupervisedUsers, setShowOnlySupervisedUsers] = useState(false);

  const [searchParams] = useSearchParams();

  // Depends on the value, not on `searchParams`: a table write to the address bar must not reset the switch.
  const onlySupervisedUsersParam = searchParams.get('onlySupervisedUsers');
  useEffect(() => {
    if (onlySupervisedUsersParam) {
      setShowOnlySupervisedUsers(onlySupervisedUsersParam === 'true');
    }
  }, [onlySupervisedUsersParam]);

  const {
    isEditing,
    setIsEditing,
    panelVisible,
    closePanel,
    selectedResource,
    openPanel: originalOpenPanel,
    isDeleting,
    setIsDeleting,
  } = useResourcePanel<Routine>(undefined);

  const openPanel = useCallback(
    async (isEditing: boolean, resourceOrId?: Routine | string) => {
      if (typeof resourceOrId === 'string') {
        const routine = await get<Routine>(`/routines/${resourceOrId}`);
        originalOpenPanel(isEditing, routine);
      } else {
        originalOpenPanel(isEditing, resourceOrId);
      }
    },
    [get, originalOpenPanel],
  );

  const handleDelete = useCallback(
    async (id: string | undefined): Promise<boolean> => {
      if (!id || !organization?.id) return false;
      setIsDeleting(true);
      const response = await del<Routine>(`/routines/${id}`, {
        errorMessages: {
          summary: 'No se pudo eliminar la rutina',
          defaultDetail: 'Hubo un error al intentar borrar la rutina',
        },
        successMessage: {
          summary: 'Rutina eliminada',
          detail: 'La rutina se ha eliminado correctamente',
        },
      });
      setIsDeleting(false);
      // Deleting a routine can add its client to "patients without routine"; refresh the alert now, not at its next poll.
      if (response) mutate(`/users/${authUser?.id}/routine-alert`);
      return !!response;
    },
    [organization, del, setIsDeleting, authUser?.id],
  );

  const tableHeaders: TableColumn<RoutineTableEntry>[] = useMemo(
    () => [
      {
        label: 'Nombre',
        accessor: 'name',
        type: 'string',
        isTitle: true,
      },
      {
        label: 'Tipo',
        accessor: 'routineType',
        colorFromCallBack: (routineType) => {
          return routineType === 'user' ? 'primary' : 'contrast';
        },
        textKeyFromCallBack: (routineType) => {
          return routineType === 'user' ? 'Usuario' : 'Plantilla';
        },
        type: 'tag',
      },
      {
        label: 'Duración (días)',
        accessor: 'duration',
        type: 'number',
      },
      {
        label: 'Días restantes',
        accessor: 'remainingDays',
        type: 'number',
        severityCallback: (_, row) => {
          if (row.isExpired) return null;
          return getRowDaysRemainingSeverity(_, row);
        },
      },
      {
        label: 'Progreso',
        accessor: 'progress',
        type: 'progress',
        // type-only cast: `progress` is optional in the Routine type (types/Fisiofit/routine.ts), so it can be undefined at runtime (#234)
        percentageValueFromCallBack: (progress) => progress as number | null,
        severityCallback: (_, row) => {
          const { hasStarted } = getRoutineDays(row.localStartDate, row.duration);
          if (!hasStarted) return null;
          return getRowDaysRemainingSeverity(_, row) ?? 'success';
        },
        overrideMessage: (progress, row) => {
          if (!row.externalUser) return null;
          if (row.isExpired) {
            return 'Rutina caducada';
          }
          return progress === null ? 'Aún no ha comenzado' : null;
        },
      },
      {
        label: 'Próxima rutina',
        accessor: 'hasNextRoutine',
        type: 'boolean',
        trueText: 'Sí',
        falseText: 'No',
        trueIcon: PrimeIcons.CHECK_CIRCLE,
        falseIcon: PrimeIcons.TIMES_CIRCLE,
        allowNullFilter: true,
      },
    ],
    [],
  );

  const options: TableAction[] = useMemo(
    () => [
      {
        label: 'Ver',
        onClick: (id) => {
          openPanel(false, id);
        },
        hasPermission: userHasEditFeature,
      },
      {
        label: 'Editar',
        onClick: (id) => {
          openPanel(true, id);
        },
        hasPermission: userHasEditFeature,
      },
      {
        label: 'Eliminar',
        onClick: async (id) => {
          const routine = selectedResource?.id === id ? selectedResource : await get<Routine>(`/routines/${id}`);
          showDeleteDialog(() => handleDelete(id), getRoutineDeleteConfirmationDialogProps(routine, false));
        },
        hasPermission: userHasEditFeature,
      },
    ],
    [openPanel, userHasEditFeature, handleDelete, selectedResource, get],
  );

  const allowedFeatures: AllowedFeatures = {
    edit: [ALL_FEATURES.ROUTINES_WRITE],
    delete: [ALL_FEATURES.ROUTINES_WRITE],
    create: [ALL_FEATURES.ROUTINES_WRITE],
  };

  const endpoint = useMemo(() => {
    if (!organization) return '';
    const baseEndpoint = `/organizations/${organization.id}/routines/paginated`;
    if (showOnlySupervisedUsers && authUser?.id) {
      return `${baseEndpoint}?supervisorId=${authUser.id}`;
    }
    return baseEndpoint;
  }, [organization, showOnlySupervisedUsers, authUser?.id]);

  const renderHeaderOptions = useCallback(
    (fetchAllForExport: () => Promise<RoutineTableEntry[]>) => (
      <div className='routines-header-options'>
        {userHasEditFeature && (
          <>
            <Button
              label='Añadir rutina'
              outlined
              rounded
              icon={PrimeIcons.PLUS}
              onClick={() => {
                openPanel(false);
              }}
            />
            <ExportButton fetchData={fetchAllForExport} columns={tableHeaders} fileName='Rutinas' disabled={isDeleting} />
            <Switch
              id='show-supervised'
              label='Mis pacientes'
              primeSwitchProps={{
                checked: showOnlySupervisedUsers,
                onChange: (e) => setShowOnlySupervisedUsers(e.value),
              }}
            />
          </>
        )}
      </div>
    ),
    [userHasEditFeature, openPanel, tableHeaders, isDeleting, showOnlySupervisedUsers],
  );

  return (
    <PageTemplate className='employees-page' title='Rutinas'>
      <ServerResponsiveTable
        tableId='routines'
        endpoint={endpoint}
        disabled={isDeleting}
        columns={tableHeaders}
        options={options}
        headerOptions={renderHeaderOptions}
        onRowClick={(row) => {
          openPanel(false, row.id);
        }}
      />
      <RoutinePanel
        resource={selectedResource}
        visible={panelVisible}
        onHide={closePanel}
        onIsEditingChange={(value) => setIsEditing(value)}
        isEditing={isEditing}
        onDeleteResource={handleDelete}
        isDeleting={isDeleting}
        allowedFeatures={allowedFeatures}
      />
    </PageTemplate>
  );
};
